interface CookieWindow extends Window {
  OptanonWrapper: () => void;
  OnetrustActiveGroups: string;
}

/**
 * Hook for initializing and managing the CookiePro consent script across different environments.
 *
 * @param environment - The deployment environment ('sandbox', 'development', 'qa', or 'production')
 * @returns An object containing utility functions for CookiePro script management
 * @returns {Function} initialise - Initializes the CookiePro script with domain-specific configuration
 * @returns {Function} doesScriptExist - Checks if a script element exists in the DOM by ID
 *
 * @example
 * ```typescript
 * const { initialise, doesScriptExist } = useCookieProInitialiser('production');
 *
 * await initialise(
 *   'abc123def456',
 *   'https://cdn.cookielaw.org/scripttags/abc123def456/otSDKStub.js',
 *   ['production'],
 *   'onetrust-consent-script'
 * );
 * ```
 *
 *
 *
 * @remarks
 * - The hook sets up the OptanonWrapper callback to handle cookie consent changes
 * - Automatically reloads the page if previously consented categories are revoked
 * - Only loads the CookiePro script if the current environment is included in the enableIn array
 * - Removes any existing script with the same ID before creating a new one
 * - Logs success messages only in non-production environments
 */
export function cookieProInitialiser(environment: 'sandbox' | 'development' | 'qa' | 'production') {
  const doesScriptExist = (id: string): boolean => {
    return document.querySelector(`#${id}`) !== null;
  };

  const checkUpdatedActiveCookieCategories = (
    currentCookieCategories: string[],
    activeCookieCategories: string[],
  ): string[] => {
    currentCookieCategories.forEach((currentCategory) => {
      const categoryStillActive = checkIfCategoryStillActive(
        currentCategory,
        activeCookieCategories,
      );
      if (!categoryStillActive) {
        location.reload();
      }
    });
    return activeCookieCategories;
  };

  const checkIfCategoryStillActive = (
    currentCategory: string,
    activeCookieCategories: string[],
  ): string | undefined => {
    return activeCookieCategories.find((ac: string) => ac === currentCategory);
  };
  /**
   * Initializes the CookiePro script with domain-specific configuration.
   *
   * @param brandScriptId - The ID of the CookiePro script for the specific instance of implementation. More info here https://domgen.atlassian.net/wiki/spaces/Sales/pages/12218236967/CookiePro
   * @param cookieProSrc - The deployment JavaScript script source URL for CookiePro.
   * @param enableIn - The list of environments in which the CookiePro script should be enabled.
   * @param id - The DOM ID to assign to the CookiePro script element.
   * @returns {Promise<void>} A promise that resolves when the CookiePro script has successfully loaded or when loading is skipped for the current environment.
   * @throws {Error} If the CookiePro script fails to load for the given source URL.
   */
  const initialise = (
    brandScriptId: string,
    cookieProSrc: string,
    enableIn: ('sandbox' | 'development' | 'qa' | 'production')[],
    id: string,
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (doesScriptExist(id)) {
        const existingScript = document.getElementById(id);
        existingScript?.remove();
      }

      const head = document.head;

      if (enableIn.includes(environment)) {
        const script: HTMLScriptElement = document.createElement('script');
        script.src = cookieProSrc;
        script.setAttribute('data-domain-script', brandScriptId);
        script.setAttribute('data-document-language', 'true');
        script.setAttribute('id', id);

        head.insertBefore(script, head.childNodes[0]);

        let currentCookieCategories: string[] = [];

        // OptanonWrapper executes on app load, and on every change of cookie consent
        (window as unknown as CookieWindow).OptanonWrapper = () => {
          const activeCookieCategories = (
            window as unknown as CookieWindow
          ).OnetrustActiveGroups.split(',').filter((g: string) => Boolean(g));
          currentCookieCategories = checkUpdatedActiveCookieCategories(
            currentCookieCategories,
            activeCookieCategories,
          );
        };

        script.onload = () => {
          if (environment !== 'production') {
            console.log(`CookiePro script loaded.`);
          }

          resolve();
        };

        script.onerror = (event: string | Event) => {
          console.error(`Error loading script with src ${cookieProSrc}.`, event);
          reject(new Error(`Failed to load script with src ${cookieProSrc}.`));
        };
      } else {
        resolve();
      }
    });
  };

  return { initialise, doesScriptExist };
}
